import { getRecaptchaHeaders, generateRecaptchaTokenWithFallback } from '../utils/recaptcha.js';

// Medicine Event API Service

const API_BASE_URL = (!process.env.NODE_ENV || process.env.NODE_ENV === 'development')
  ? 'http://127.0.0.1:8000/api'
  : window.location.origin + '/api';


// Legacy function - kept for backward compatibility with other endpoints
const getRecaptchaHeadersLegacy = () => ({
  'X-Recaptcha-Token': '', // Empty for non-start-session requests
  'X-Recaptcha-Version': 'v3'
});

// Utility to generate random user ID for anonymous users
const generateRandomUserId = () => {
  return `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Enhanced error handling
class ApiError extends Error {
  constructor(message, status, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

// Modern API service class with proper error handling and TypeScript-like structure
class MedEventApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...getRecaptchaHeadersLegacy() // Use legacy headers for backward compatibility
    };
  }

  // Private method for making HTTP requests with proper error handling
  async _makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const config = {
      headers: this.defaultHeaders,
      cache: 'no-cache',
      ...options
    };

    // Merge custom headers if provided
    if (options.headers) {
      config.headers = { ...config.headers, ...options.headers };
    }


    try {
      const response = await fetch(url, config);
      const data = await response.json();

      // Handle API errors - but allow null responses (they indicate completion)
      if (!response.ok || (data && data.detail)) {
        throw new ApiError(
          data?.detail || data?.error || 'Request failed',
          response.status,
          data
        );
      }

      return { success: true, data };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error.message || 'Network error',
        0,
        { originalError: error }
      );
    }
  }

  // Build query string from parameters
  _buildQueryString(params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });
    return searchParams.toString();
  }

    /**
   * Get active session for a user
   * @param {string} webRandomUserId - Random user ID for anonymous users  
   * @returns {Promise<Object>} Active session data
   */
  async getActiveSession(webRandomUserId) {
    const queryParams = this._buildQueryString({
      web_random_user_id: webRandomUserId
    });
    
    const result = await this._makeRequest(`/get_active_session?${queryParams}`);
    return result;
  }

  /**
   * Start a quiz session with reCAPTCHA v3/v2 fallback
   * @param {string} sessionId - Session identifier
   * @param {string} webRandomUserId - Random user ID for anonymous users
   * @returns {Promise<Object>} Session start data with first question
   */
  async startSession(sessionId, webRandomUserId) {
    return new Promise((resolve, reject) => {
      const attemptStartSession = async (recaptchaToken, version = 'v3') => {
        try {
          const recaptchaHeaders = await getRecaptchaHeaders(recaptchaToken, version);
          const queryParams = this._buildQueryString({
            session_id: sessionId,
            web_random_user_id: webRandomUserId
          });

          const result = await this._makeRequest(`/start_session?${queryParams}`, {
            headers: recaptchaHeaders
          });

          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      const handleV3Failed = () => {
        if (window.__showRecaptchaV2Modal) {
          window.__showRecaptchaV2Modal(
            (v2Token) => {
              attemptStartSession(v2Token, 'v2');
            },
            (error) => {
              reject(new ApiError('Verifica reCAPTCHA annullata o fallita', 403, { 
                recaptchaError: error,
                userCancelled: error.includes('cancelled'),
                shouldNavigateBack: true
              }));
            }
          );
        } else {
          reject(new ApiError('Verifica reCAPTCHA non disponibile', 403));
        }
      };

      generateRecaptchaTokenWithFallback('start_session', handleV3Failed)
        .then(result => {
          if (result.success) {
            attemptStartSession(result.token, result.version);
          } else if (!result.requiresV2) {
            reject(new ApiError('Verifica reCAPTCHA non disponibile', 503));
          }
        })
        .catch(error => {
          reject(new ApiError('Errore generazione token reCAPTCHA', 503, { recaptchaError: error }));
        });
    });
  }

  /**
   * Respond to a question in the quiz
   * @param {Object} params - Response parameters
   * @param {string} params.sessionId - Session identifier
   * @param {number} params.questionNumber - Current question number
   * @param {number} params.answerNumber - Selected answer index (for multiple choice)
   * @param {string} params.answerText - Text answer (for fill-in questions)
   * @param {string} params.webRandomUserId - Random user ID
   * @returns {Promise<Object>} Next question data or completion status
   */
  async respondToQuestion({ sessionId, questionNumber, answerNumber, answerText, webRandomUserId }) {
    const queryParams = this._buildQueryString({
      session_id: sessionId,
      question_number: questionNumber,
      answer_number: answerNumber,
      answer_text: answerText,
      web_random_user_id: webRandomUserId
    });
    
    return this._makeRequest(`/respond_to_question?${queryParams}`);
  }

  /**
   * End a quiz session (implicit - no specific endpoint mentioned but commonly needed)
   * This would typically be called when the quiz is completed
   */
  async endSession(sessionId, webRandomUserId) {
    // Implementation depends on actual API - placeholder for now
    const queryParams = this._buildQueryString({
      session_id: sessionId,
      web_random_user_id: webRandomUserId
    });
    
    // This endpoint might not exist yet, but following the pattern
    return this._makeRequest(`/end_session?${queryParams}`);
  }
}

// Create and export a singleton instance
const medEventApi = new MedEventApiService();

// Export both the class and the instance for flexibility
export { MedEventApiService, generateRandomUserId, ApiError };
export default medEventApi;
